import { useRef, useState } from "react";
import { FolderOpen, Upload, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";

export function NominalUpload({ disabled, busy, onUpload }: {
  disabled: boolean; busy: boolean; onUpload: (file: File) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  return <div className="space-y-2 border-t pt-3">
    <h2 className="text-sm font-bold">Add nominal trial balance 17012</h2>
    <p className="text-xs text-slate-600">Select the matching monthly period, then upload the complete Merlin Transaction Details export (XLS or XLSX).</p>
    <input ref={ref} type="file" accept=".xls,.xlsx" className="hidden" disabled={disabled || busy}
      onChange={e=>setFile(e.target.files?.[0] ?? null)} data-testid="input-nominal-file" aria-label="Nominal 17012 file" />
    <Button variant="outline" className="w-full gap-2 whitespace-normal h-auto py-3" disabled={disabled || busy}
      onClick={()=>ref.current?.click()} data-testid="button-choose-nominal"><FolderOpen className="w-5 h-5 shrink-0" />Choose nominal XLS / XLSX</Button>
    <p className="text-xs break-all" role="status">{file?.name ?? "No nominal report selected"}</p>
    <Button className="w-full gap-2" size="sm" disabled={disabled || busy || !file} onClick={()=>file && onUpload(file)}
      data-testid="button-import-nominal">{busy?<Loader2 className="w-4 h-4 animate-spin" />:<Upload className="w-4 h-4" />}Import and match</Button>
    {disabled && <p className="text-xs text-amber-800">Choose an unsigned period first.</p>}
  </div>;
}

export function DeletePeriodButton({ label, disabled, onDelete }: {
  label: string; disabled: boolean; onDelete: (notes: string) => Promise<boolean>;
}) {
  const [open,setOpen] = useState(false);
  const [notes,setNotes] = useState("");
  return <>
    <Button size="sm" variant="outline" disabled={disabled} className="gap-1 text-red-700" onClick={()=>setOpen(true)}
      data-testid="button-delete-period"><Trash2 className="w-4 h-4" />Delete period</Button>
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {label}?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes the draft period from the active list and clears affected matches. Source files and audit history are retained.
            Deleting a statement removes its bank entries from active reviews; deleting a monthly review keeps separately imported statements.
            Other overlapping draft reviews will need their coverage confirmed again. Nothing is changed in Merlin.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <label className="text-sm">Reason for deleting
          <textarea className="mt-1 w-full rounded border p-2" value={notes} onChange={e=>setNotes(e.target.value)}
            maxLength={4000} data-testid="input-delete-reason" />
        </label>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={disabled}>Cancel</AlertDialogCancel>
          <Button variant="destructive" disabled={disabled || notes.trim().length<5}
            onClick={async()=>{if(await onDelete(notes.trim())) {setOpen(false);setNotes("");}}}
            data-testid="button-confirm-delete-period">Delete period</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
}
